import { generateBetItemId } from '../utils/ticketHelpers';
import { customBetLabel } from '../utils/customBet';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from '../i18n';
import { resolveMarketName, resolveOutcomeName, formatOddsButtonLabel } from '../utils/marketTranslations';
import { BetItem } from './BettingSlipDrawer';
import { EventSnapshot, Market, Outcome, CustomBetAvailableSelectionsOut, CustomBetFilterOut, CustomBetMarket, CustomBetOutcome } from '../api/types';
import { OddsButton } from './OddsButton';
import { useFavorites } from '../contexts/FavoritesContext';
import { getMatchStreamInfo } from '../api/stream';
import { getPreEvents, getCustomBetAvailableSelections, filterCustomBetSelections } from '../api/api';
import { GLOBAL_MARKET_ORDER, EXTRA_MARKET_NAMES_LANGPACK, DEFAULT_MARKET_ORDER } from '../constants/marketGroups';

type PanelType = 'stream' | 'tracker' | 'stats' | 'lineup' | 'timeline';

interface MatchDetailPageProps {
  event: EventSnapshot;
  onBack: () => void;
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
  onUpdateBet?: (id: string, patch: Partial<BetItem>) => void;
  onLoginClick: () => void;
  /** Which market tab to open on mount — used when arriving from an MK+ card. */
  initialMarketTab?: 'all' | 'builder';
}

const LockIcon = () => (<svg className="w-4 h-4 text-gray-400 block" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>);

// Over/Under style outcomes must render "+" (over) on the LEFT and "-" (under)
// on the RIGHT. Plain Über/Unter markets already arrive over-first, but combined
// markets like "Tipp & Über/Unter" come minus-first from the language pack
// (e.g. "1 & 2.5 -" before "1 & 2.5 +"). Within each pair that shares the same
// base label the "+" outcome is moved first; pair order itself (1, X, 2) and
// every list without a trailing sign are left untouched.
function reorderPlusMinusPairs<T extends { btnText: string }>(arr: T[]): T[] {
  const sign = (t: string) => {
    const s = t.trim();
    if (s.endsWith('+')) return '+';
    if (s.endsWith('-') || s.endsWith('−')) return '-';
    return null;
  };
  if (arr.length < 2 || arr.some((o) => !sign(o.btnText))) return arr;
  const base = (t: string) => t.trim().replace(/[+\-−]$/, '').trim();
  const order: string[] = [];
  const groups = new Map<string, T[]>();
  for (const o of arr) {
    const b = base(o.btnText);
    if (!groups.has(b)) { groups.set(b, []); order.push(b); }
    groups.get(b)!.push(o);
  }
  const out: T[] = [];
  for (const b of order) {
    const g = groups.get(b)!;
    g.sort((a, c) => (sign(a.btnText) === '+' ? 0 : 1) - (sign(c.btnText) === '+' ? 0 : 1));
    out.push(...g);
  }
  return out;
}

export function formatOdds(val: string | number): string {
  const n = parseFloat(String(val));
  if (isNaN(n) || n < 1.01) return '-';
  return n.toFixed(2).replace('.', ',');
}

function formatClock(ev: EventSnapshot, t?: (k: string) => string): string {
  const sport = ev.event?.sport_name;

  const sportId = ev.event?.sport_id;

  if (sport === 'Baseball' || sportId === 'sr:sport:3') {
    const text = ev.status?.match_status_text || '';
    const numMatch = text.match(/\d+/);
    if (numMatch) {
      return `${numMatch[0]}/9`;
    }
    if (text.toLowerCase().includes('break')) {
      const ps = ev.status?.period_scores;
      if (ps && ps.length > 0) {
        return `${ps.length}/9`;
      }
      return 'Break';
    }
    if (text) return text;
  }

  const useStatusText = sport === 'Basketball' || sport === 'Tennis' || sport === 'Table Tennis' || sport === 'Volleyball' || sportId === 'sr:sport:2' || sportId === 'sr:sport:5' || sportId === 'sr:sport:20' || sportId === 'sr:sport:23';

  if (useStatusText && ev.status?.match_status_text) {
    return ev.status.match_status_text.replace(/\b(?:1st|first)\b/gi, '1.').replace(/\b(?:2nd|second)\b/gi, '2.').replace(/\b(?:3rd|third)\b/gi, '3.').replace(/\b(?:4th|fourth)\b/gi, '4.').replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1.');
  }

  const stLower = (ev.status?.match_status_text || '').toLowerCase();
  if (stLower === 'halftime' || stLower === 'halbzeit' || stLower === 'devre arası' || stLower === 'ht') {
    return t ? t('live.ht') : 'HT';
  }
  if (stLower === 'ended' || stLower === 'closed' || stLower === 'finished' || stLower === 'ft' || stLower === 'beendet' || stLower === 'bitti' || stLower === 'ms') {
    return t ? t('live.ft') : 'FT';
  }

  if (ev.status?.clock) {
    const minStr = ev.status.clock.split(':')[0];
    if (minStr.includes('+')) {
      return minStr + "'";
    }
    const minNum = parseInt(minStr, 10);
    if (!isNaN(minNum)) {
      const displayMin = minNum + 1;

      if (sport === 'Soccer' || sportId === 'sr:sport:1') {
        const matchStatusCode = (ev.status as any)?.match_status;
        const isFirstHalf = stLower.includes('1.') || stLower.includes('1st') || stLower.includes('first') || stLower.includes('ilk') || stLower === '1' || matchStatusCode === '6' || matchStatusCode === 6;
        const isExtraTime = stLower.includes('et') || stLower.includes('extra') || stLower.includes('uzatma') || stLower.includes('verläng') || stLower.includes('verlaeng') || stLower.includes('overtime') || matchStatusCode === '41' || matchStatusCode === '42' || matchStatusCode === 41 || matchStatusCode === 42;

        const is90 = minStr === '90';
        const is45 = minStr === '45';

        if ((is90 && !isExtraTime && !isFirstHalf) || (is45 && isFirstHalf)) {
          const baseMin = is90 ? 90 : 45;
          // @ts-ignore: stoppage_time exists in SSE payload
          const stoppage = ev.status?.stoppage_time;
          if (stoppage && stoppage !== '0:00' && typeof stoppage === 'string') {
            const parts = stoppage.split(':');
            if (parts.length === 2) {
              const sMin = parseInt(parts[0], 10);
              const sSec = parseInt(parts[1], 10);
              if (!isNaN(sMin) && !isNaN(sSec)) {
                let added = sMin;
                if (sSec > 0 || sMin === 0) added += 1;
                return `${baseMin}+${added}'`;
              }
            }
          }
          return `${baseMin}'`;
        }

        if (isFirstHalf && displayMin > 45 && !isExtraTime) {
          return `45+${displayMin - 45}'`;
        }
        if (displayMin > 90 && !isExtraTime && !isFirstHalf) {
          return `90+${displayMin - 90}'`;
        }
      }

      return displayMin + "'";
    }
    return minStr + "'";
  }
  if (ev.status?.match_status_text) {
    return ev.status.match_status_text.replace(/\b(?:1st|first)\b/gi, '1.').replace(/\b(?:2nd|second)\b/gi, '2.').replace(/\b(?:3rd|third)\b/gi, '3.').replace(/\b(?:4th|fourth)\b/gi, '4.').replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1.');
  }
  return 'Live';
}

function formatMatchTime(ev: EventSnapshot): string {
  const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
  if (!starts) return '';
  const d = new Date(starts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const timeStr = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `${timeStr} heute`;
  if (isTomorrow) return `${timeStr} morgen`;
  return `${timeStr} ${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}`;
}

// Market display configs

function getSpecifierLabel(market: Market): string {
  if (!market.specifiers) return '';
  const total = market.specifiers['total'];
  if (total) return total;
  const hcp = market.specifiers['hcp'];
  if (hcp) return hcp;
  const goalnr = market.specifiers['goalnr'];
  if (goalnr) return goalnr;

  // fallback for any other single specifier (skip sr: values like sr:player:209374)
  const vals = Object.entries(market.specifiers)
    .filter(([k, v]) => !String(v).startsWith('sr:'))
    .map(([k, v]) => v);
  if (vals.length === 1) return vals[0];

  return '';
}

const JerseyIcon: React.FC<{ color?: string, className?: string }> = ({ color = '#c0392b', className = "w-12 h-12" }) => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M14 6 L6 14 L12 16 L12 40 H36 L36 16 L42 14 L34 6 Q29 10 24 10 Q19 10 14 6Z" fill={color} stroke="white" strokeWidth="1.5" />
    <rect x="18" y="6" width="12" height="8" rx="2" fill="white" opacity="0.7" />
  </svg>
);

const MatchDetailPage: React.FC<MatchDetailPageProps> = ({
  event: ev,
  onBack,
  bets,
  onAddBet,
  onRemoveBet,
  onUpdateBet,
  onLoginClick,
  initialMarketTab,
}) => {
  const { toggleFavorite, isFavorite } = useFavorites();
  const sportIdStr = ev.event?.sport_id;
  const sportId = sportIdStr ? parseInt(sportIdStr.split(':')[2] || '1', 10) : 1;
  const sportConfig = GLOBAL_MARKET_ORDER[sportId] || DEFAULT_MARKET_ORDER;
  const defaultTab = sportConfig.extraMarketNames[0] || 'Hauptwetten';

  const { locale, t } = useTranslation();
  const [collapsedMarkets, setCollapsedMarkets] = useState<Record<string, boolean>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [heroVisible, setHeroVisible] = useState(true);
  const heroRef = useRef<HTMLDivElement>(null);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<PanelType | null>('tracker');
  const [leagueEvents, setLeagueEvents] = useState<EventSnapshot[] | null>(null);

  // ── Market view tabs: All Markets vs Bet Builder+ (CustomBet) ────────────────
  const [marketTab, setMarketTab] = useState<'all' | 'builder'>('all');
  const [bbData, setBbData] = useState<CustomBetAvailableSelectionsOut | null>(null);
  const [bbLoading, setBbLoading] = useState(false);
  const [bbError, setBbError] = useState<string | null>(null);
  const [bbSelected, setBbSelected] = useState<{ market_id: string; specifiers: string; outcome_id: string }[]>([]);
  const [bbFilter, setBbFilter] = useState<CustomBetFilterOut | null>(null);
  const [bbFilterLoading, setBbFilterLoading] = useState(false);

  // True once the user has actually picked/cleared something in the builder. Until
  // then an empty selection must not wipe an MK+ that came from a MatchKombi card.
  const bbTouchedRef = useRef(false);
  // Id of the ordinary single bet the builder currently owns (one pick only).
  const bbSingleIdRef = useRef<string | null>(null);

  // Reset the builder state whenever the event (or requested initial tab) changes.
  useEffect(() => {
    const isSoccer = ev?.event.sport_id === 'sr:sport:1';
    setMarketTab((isSoccer ? initialMarketTab : 'all') ?? 'all');
    setBbData(null);
    setBbError(null);
    setBbSelected([]);
    setBbFilter(null);
    bbTouchedRef.current = false;
    bbSingleIdRef.current = null;
  }, [ev?.event_id, initialMarketTab, ev?.event.sport_id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [ev?.event_id]);

  // Load CustomBet available selections on component mount (for soccer matches) to determine tab visibility
  useEffect(() => {
    if (ev?.event.sport_id !== 'sr:sport:1' || bbData) return;
    let active = true;
    setBbLoading(true);
    setBbError(null);
    getCustomBetAvailableSelections(ev.event_id, { locale, scope: ev.scope })
      .then((d) => {
        if (active) {
          setBbData(d);
          if (!d.custombet_available || d.markets.length === 0) {
            setMarketTab('all');
          }
        }
      })
      .catch((e) => {
        if (active) {
          setBbError(e?.message || 'load_error');
          setMarketTab('all');
        }
      })
      .finally(() => { if (active) setBbLoading(false); });
    return () => { active = false; };
  }, [bbData, ev?.event.sport_id, ev?.event_id, ev?.scope, locale]);

  // Re-price / re-check compatibility after every selection change.
  useEffect(() => {
    if (bbSelected.length === 0) { setBbFilter(null); return; }
    let active = true;
    // Drop the previous price immediately: it belongs to the old selection set.
    setBbFilter(null);
    setBbFilterLoading(true);
    filterCustomBetSelections(
      ev.event_id,
      { scope: ev.scope, selections: bbSelected.map((s) => ({ market_id: s.market_id, specifiers: s.specifiers, outcome_id: s.outcome_id })) },
      { locale }
    )
      .then((f) => { if (active) setBbFilter(f); })
      .catch(() => { if (active) setBbFilter(null); })
      .finally(() => { if (active) setBbFilterLoading(false); });
    return () => { active = false; };
  }, [bbSelected, ev.event_id, ev.scope, locale]);

  const isBbOutcomeSelected = (marketId: string, spec: string, outcomeId: string) =>
    bbSelected.some((s) => s.market_id === marketId && s.specifiers === spec && s.outcome_id === outcomeId);

  const toggleBbOutcome = (marketId: string, spec: string, outcomeId: string) => {
    bbTouchedRef.current = true;
    setBbSelected((prev) => {
      const exists = prev.some((s) => s.market_id === marketId && s.specifiers === spec && s.outcome_id === outcomeId);
      if (exists) return prev.filter((s) => !(s.market_id === marketId && s.specifiers === spec && s.outcome_id === outcomeId));
      return [...prev, { market_id: marketId, specifiers: spec, outcome_id: outcomeId }];
    });
  };

  const bbIncompatibleKeys = useMemo(() => {
    const set = new Set<string>();
    (bbFilter?.incompatible || []).forEach((i) => set.add(`${i.market_id}|${i.specifiers || ''}|${i.outcome_id}`));
    return set;
  }, [bbFilter]);

  useEffect(() => {
    let active = true;
    async function checkStream() {
      const info = await getMatchStreamInfo(ev.event_id);
      if (active) {
        setStreamUrl(info?.watch_url ?? null);
      }
    }
    checkStream();
    return () => { active = false; };
  }, [ev.event_id]);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let active = true;
    async function loadLeagueEvents() {
      if (!ev.event.tournament_id) return;
      try {
        const data = await getPreEvents({ tournament_id: ev.event.tournament_id });
        if (active) setLeagueEvents(data.items);
      } catch {
        if (active) setLeagueEvents([]);
      }
    }
    loadLeagueEvents();
    return () => { active = false; };
  }, [ev.event.tournament_id]);

  const trackerUrl = `https://tracker-5v0.pages.dev/static/tracker?collapse=&layout=single&board=disable&matchId=${encodeURIComponent(ev.event_id)}&lang=de`;

  const handlePanelClick = (panel: PanelType) => setActivePanel((current) => (current === panel ? null : panel));

  const isLive = ev.scope === 'live';

  const simulatedMarkets: Market[] = [...(ev.markets ?? [])];

  const marketsByGroup = useMemo(() => {
    const result: Array<{ groupName: string, label: string, markets: Market[][] }> = [];
    const validMarkets = simulatedMarkets.filter((m) => m.status !== -1 && m.outcomes.length > 0);
    const renderedMarketIds = new Set<string>();

    const getGroupKey = (m: Market) => {
      let key = m.market_id;
      const originalName = (m.name || "").toLowerCase();
      const appendAttrToTitle = (originalName.includes('goal') && originalName.includes('{!goalnr}')) ||
        (originalName.includes('corner') && originalName.includes('{!cornernr}')) ||
        m.outcomes.length >= 4;
      if (appendAttrToTitle && m.specifiers) {
        key = m.market_id + '-' + JSON.stringify(m.specifiers);
      }
      return key;
    };

    sportConfig.extraMarketNames.filter(n => n !== 'All').forEach(groupName => {
      const groupConfig = sportConfig[groupName];
      if (!groupConfig || !groupConfig.marketIds) return;

      const groupMarkets = validMarkets.filter(m => groupConfig.marketIds.includes(parseInt(m.market_id)));
      if (groupMarkets.length === 0) return;

      // Dynamic reordering based on live/pre-match state
      // Market 5035 (Early Payout): pre-match 2nd position (after 1x2)
      // Market 7 (Kalan Süre): live 2nd position, pre-match 4th position
      let effectiveMarketIds = [...groupConfig.marketIds];

      const id1Idx = effectiveMarketIds.indexOf(1);

      if (!isLive) {
        // Pre-match ordering after 1x2
        const id7Idx = effectiveMarketIds.indexOf(7);
        if (id7Idx !== -1) {
          effectiveMarketIds.splice(id7Idx, 1);
          // After 1x2(1), 5035, 8, 9 → position 4 (index after 9)
          const pos1 = effectiveMarketIds.indexOf(1);
          const insertAt = pos1 !== -1 ? Math.min(pos1 + 4, effectiveMarketIds.length) : 4;
          effectiveMarketIds.splice(insertAt, 0, 7);
        }
      } else {
        // Live ordering: 1x2, 7 (Kalan Süre), 8, 9, ...
        const id7Idx = effectiveMarketIds.indexOf(7);
        if (id7Idx !== -1) {
          effectiveMarketIds.splice(id7Idx, 1);
          effectiveMarketIds.splice(id1Idx !== -1 ? id1Idx + 1 : 0, 0, 7);
        }
      }

      groupMarkets.sort((a, b) => {
        const idxA = effectiveMarketIds.indexOf(parseInt(a.market_id));
        const idxB = effectiveMarketIds.indexOf(parseInt(b.market_id));
        return (idxA !== -1 ? idxA : 9999) - (idxB !== -1 ? idxB : 9999) || a.market_id.localeCompare(b.market_id);
      });

      // Group by market_id (or market_id + specifiers for isolated ones)
      const groups: Record<string, Market[]> = {};
      groupMarkets.forEach(m => {
        const key = getGroupKey(m);
        if (!groups[key]) groups[key] = [];
        groups[key].push(m);
      });

      const uniqueGroups: Market[][] = [];
      const seen = new Set<string>();
      groupMarkets.forEach(m => {
        const key = getGroupKey(m);
        if (!seen.has(key)) {
          seen.add(key);
          uniqueGroups.push(groups[key]);
          renderedMarketIds.add(m.market_id);
        }
      });

      const groupLabel = EXTRA_MARKET_NAMES_LANGPACK[locale]?.[groupName] || EXTRA_MARKET_NAMES_LANGPACK['en']?.[groupName] || groupName;
      result.push({ groupName, label: groupLabel, markets: uniqueGroups });
    });

    // Handle any remaining markets not captured by the specific groups
    const remainingMarkets = validMarkets.filter(m => !renderedMarketIds.has(m.market_id));
    if (remainingMarkets.length > 0) {
      remainingMarkets.sort((a, b) => {
        const groupConfig = sportConfig['All'];
        if (groupConfig && groupConfig.marketIds) {
          const idxA = groupConfig.marketIds.indexOf(parseInt(a.market_id));
          const idxB = groupConfig.marketIds.indexOf(parseInt(b.market_id));
          return (idxA !== -1 ? idxA : 9999) - (idxB !== -1 ? idxB : 9999) || a.market_id.localeCompare(b.market_id);
        }
        return a.market_id.localeCompare(b.market_id);
      });

      const groups: Record<string, Market[]> = {};
      remainingMarkets.forEach(m => {
        const key = getGroupKey(m);
        if (!groups[key]) groups[key] = [];
        groups[key].push(m);
      });

      const uniqueGroups: Market[][] = [];
      const seen = new Set<string>();
      remainingMarkets.forEach(m => {
        const key = getGroupKey(m);
        if (!seen.has(key)) {
          seen.add(key);
          uniqueGroups.push(groups[key]);
        }
      });

      const otherLabel = (locale as string) === 'de' ? 'Weitere Wetten' : (locale as string) === 'tr' ? 'Diğer Bahisler' : (locale as string) === 'ru' ? 'Другие ставки' : 'Other Markets';
      result.push({ groupName: "Other", label: otherLabel, markets: uniqueGroups });
    }

    return result;
  }, [simulatedMarkets, sportConfig, locale]);

  const isBetSelected = (marketId: string, outcomeId: string, specifiers?: Record<string, string>) => {
    const betId = generateBetItemId(ev.event_id, marketId, outcomeId, specifiers);
    return bets.some((b) => b.id === betId || b.id === `desk-${betId}`);
  };

  const getExactBetId = (marketId: string, outcomeId: string, specifiers?: Record<string, string>) => {
    const betId = generateBetItemId(ev.event_id, marketId, outcomeId, specifiers);
    const bet = bets.find((b) => b.id === betId || b.id === `desk-${betId}`);
    return bet ? bet.id : betId;
  };

  const handleBetClick = (market: Market, outcome: Outcome) => {
    const specStr = market.specifiers
      ? typeof market.specifiers === 'string'
        ? market.specifiers
        : Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
      : '';

    if (isBetSelected(market.market_id, outcome.outcome_id, market.specifiers)) {
      onRemoveBet(getExactBetId(market.market_id, outcome.outcome_id, market.specifiers));
      return;
    }

    const label = resolveOutcomeName(market.market_id, outcome.outcome_id, locale, market.specifiers || {}, `${ev.event.home.name} - ${ev.event.away.name}`, outcome.name);
    let marketName = resolveMarketName(market.market_id, locale, market.specifiers || {}, market.name, ev ? `${ev.event.home.name} - ${ev.event.away.name}` : undefined) || market.name || "";
    if (marketName) {
      if (market.specifiers?.total) marketName = marketName.replace(/\{total\}/g, market.specifiers.total);
      if (market.specifiers?.goalnr) marketName = marketName.replace(/\{\!goalnr\}/g, market.specifiers.goalnr);
      if (ev) {
        marketName = marketName.replace(/\{\$competitor1\}/g, ev.event.home.name);
        marketName = marketName.replace(/\{\$competitor2\}/g, ev.event.away.name);
      }
    }
    const specLabel = getSpecifierLabel(market);
    const tippLabel = specLabel ? `${marketName} ${specLabel} – ${label}` : `${marketName} – ${label}`;
    const betId = generateBetItemId(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers);

    onAddBet({
      id: betId,
      matchId: ev.event_id,
      matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
      betType: outcome.outcome_id === '1' ? 'home' : outcome.outcome_id === '3' ? 'away' : 'draw',
      tippLabel,
      odds: formatOdds(outcome.odds),
      eventId: ev.event_id,
      marketId: market.market_id,
      outcomeId: outcome.outcome_id,
      outcomeName: (outcome as any).player_name || (outcome as any).playerName || outcome.name,
      specifiers: specStr,
    });

  };

  // Keep the slip in sync with the Bet Builder+ selection as the user picks
  // outcomes: two or more legs form the MK+ group, fewer removes it again.
  // A provider "all" group needs at least two selections, so a single pick has
  // nothing to price yet and must not appear in the slip.
  // Handlers/bets live in a ref: they change identity on every parent render, and
  // depending on them here would make this effect write to the slip in a loop.
  const cbSyncRef = useRef({ bets, onAddBet, onRemoveBet, onUpdateBet, ev });
  cbSyncRef.current = { bets, onAddBet, onRemoveBet, onUpdateBet, ev };

  const prevBuilderBetRef = useRef<string | undefined>(undefined);

  // Sync the builder UI from the slip (e.g. when an MK+ card is added from outside).
  useEffect(() => {
    if (marketTab !== 'builder') return;
    const { bets: curBets, ev } = cbSyncRef.current;

    const own = curBets.find(b => b.isCustomBet && b.eventId === ev.event_id);
    const ownHash = own ? JSON.stringify(own.customBetSelections) : undefined;

    if (ownHash !== prevBuilderBetRef.current) {
      prevBuilderBetRef.current = ownHash;

      if (own?.customBetSelections?.length) {
        const slipSelections = own.customBetSelections.map(s => ({
          market_id: s.market_id, specifiers: s.specifiers || '', outcome_id: s.outcome_id,
        }));

        const isSame = bbSelected.length === slipSelections.length && slipSelections.every(s =>
          bbSelected.some(b => b.market_id === s.market_id && (b.specifiers || '') === s.specifiers && b.outcome_id === s.outcome_id)
        );

        if (!isSame) {
          setBbSelected(slipSelections);
        }
      } else if (bbSelected.length > 0 && !bbTouchedRef.current) {
        setBbSelected([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketTab, bets, ev.event_id, bbSelected]);

  useEffect(() => {
    if (marketTab !== 'builder') return;
    const { bets: curBets, onAddBet, onRemoveBet, onUpdateBet, ev } = cbSyncRef.current;

    const builderId = `cb-${ev.event_id}`;
    const existing = curBets.find(b => b.id === builderId);
    const prevSingleId = bbSingleIdRef.current;

    const resolve = (s: { market_id: string; specifiers: string; outcome_id: string }) => {
      // Display labels come from the availability payload we already rendered.
      const market = bbData?.markets.find(m => m.market_id === s.market_id && (m.specifiers_norm || '') === s.specifiers);
      const outcome = market?.outcomes.find(o => o.outcome_id === s.outcome_id);
      return { market, outcome };
    };

    // Nothing picked: undo whatever the builder put in the slip.
    if (bbSelected.length === 0) {
      if (!bbTouchedRef.current) return;
      if (existing) onRemoveBet(builderId);
      if (prevSingleId) { onRemoveBet(prevSingleId); bbSingleIdRef.current = null; }
      return;
    }

    // A single pick is an ordinary market bet — a provider "all" group needs two.
    if (bbSelected.length === 1) {
      if (existing) onRemoveBet(builderId);

      const s = bbSelected[0];
      const { market, outcome } = resolve(s);
      const singleId = generateBetItemId(ev.event_id, s.market_id, s.outcome_id, s.specifiers);
      if (prevSingleId && prevSingleId !== singleId) onRemoveBet(prevSingleId);
      bbSingleIdRef.current = singleId;

      if (!curBets.some(b => b.id === singleId)) {
        onAddBet({
          id: singleId,
          matchId: ev.event_id,
          matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
          betType: s.outcome_id === '1' ? 'home' : s.outcome_id === '3' ? 'away' : 'draw',
          tippLabel: market && outcome ? `${market.name} – ${outcome.name}` : (outcome?.name ?? ''),
          odds: outcome ? formatOdds(outcome.odds) : '',
          eventId: ev.event_id,
          marketId: s.market_id,
          outcomeId: s.outcome_id,
          outcomeName: outcome?.name,
          specifiers: s.specifiers,
        });
      }
      return;
    }

    // Two or more picks become the MK+ group; the standalone single goes away.
    if (prevSingleId) { onRemoveBet(prevSingleId); bbSingleIdRef.current = null; }

    // An event can carry only one provider-priced group — drop a card-added one.
    const foreign = curBets.find(b => b.isCustomBet && b.eventId === ev.event_id && b.id !== builderId);
    if (foreign) onRemoveBet(foreign.id);

    const selections = bbSelected.map(s => {
      const { market, outcome } = resolve(s);
      return {
        market_id: s.market_id,
        specifiers: s.specifiers,
        outcome_id: s.outcome_id,
        market_name: market?.name,
        outcome_name: outcome?.name,
      };
    });

    // Empty while the group is being re-priced — never show a stale price.
    const odds = bbFilter?.accepted_odds ? formatOdds(bbFilter.accepted_odds) : '';

    const shared = {
      matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
      tippLabel: customBetLabel(selections.length),
      odds,
      marketId: selections[0].market_id,
      outcomeId: selections[0].outcome_id,
      specifiers: selections[0].specifiers,
      customBetSelections: selections,
    };

    if (existing && onUpdateBet) {
      onUpdateBet(existing.id, shared);
    } else if (!existing) {
      const groupId = `cb-${ev.event_id}`;
      onAddBet({
        id: groupId,
        matchId: ev.event_id,
        betType: 'home',
        eventId: ev.event_id,
        isCustomBet: true,
        customBetGroupId: groupId,
        ...shared,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketTab, bbSelected, bbFilter, bbData, ev.event_id]);

  const renderMarketGroup = (marketGroup: Market[]) => {
    if (!marketGroup || marketGroup.length === 0) return null;

    const firstMarket = marketGroup[0];
    const specMap = { ...(firstMarket.specifiers || {}) };

    // Inject player name into specMap if available in outcomes
    if (specMap.player) {
      const playerOutcome = firstMarket.outcomes?.find(o => (o as any).player_name || (o as any).playerName);
      if (playerOutcome) {
        specMap.playerName = (playerOutcome as any).player_name || (playerOutcome as any).playerName;
      }
    }

    let baseMarketName = resolveMarketName(
      firstMarket.market_id,
      locale,
      specMap,
      firstMarket.name,
      ev ? `${ev.event.home.name} - ${ev.event.away.name}` : undefined
    ) || firstMarket.name || "";

    if (baseMarketName) {
      baseMarketName = baseMarketName.replace(/\{[^}]+\}/g, '').trim();
    }

    const firstOriginalName = (firstMarket.name || "").toLowerCase();
    const hasSpecifiers = !!firstMarket.specifiers && Object.keys(firstMarket.specifiers).length > 0;
    const appendAttrToTitle = (firstOriginalName.includes('goal') && firstOriginalName.includes('{!goalnr}')) ||
      (firstOriginalName.includes('corner') && firstOriginalName.includes('{!cornernr}')) ||
      firstMarket.outcomes.length >= 4 ||
      (marketGroup.length === 1 && hasSpecifiers);

    if (appendAttrToTitle) {
      const firstSpecLabel = getSpecifierLabel(firstMarket);
      if (firstSpecLabel) {
        baseMarketName = `${baseMarketName} (${firstSpecLabel})`;
      }
    }

    return (
      <div key={`group-${firstMarket.market_id}-${appendAttrToTitle ? JSON.stringify(firstMarket.specifiers) : ''}`} className="px-3 py-2 border-b border-[#cbd5e1] bg-white last:border-b-0 flex flex-col">
        <div className="w-full mb-3 flex justify-start text-left">
          <span className="text-[14px] font-bold text-[#333] leading-tight text-left">{baseMarketName}</span>
        </div>
        <div className="flex flex-col gap-2">
          {marketGroup.map((market) => {
            let specLabel = getSpecifierLabel(market);

            const originalName = (market.name || "").toLowerCase();
            const hideLeftAttr =
              originalName.includes('exact') ||
              originalName.includes('goal range') ||
              originalName.includes('winning margin') ||
              originalName.includes('when will') ||
              originalName.includes('time of') ||
              originalName.includes('goalscorer') ||
              originalName.includes('correct score');

            if (hideLeftAttr || appendAttrToTitle) {
              specLabel = "";
            }

            const activeOutcomes = market.outcomes;
            const specStr = market.specifiers
              ? Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
              : '';

            const processedOutcomes = activeOutcomes.map((outcome) => {
              const outcomeDefaultName = (outcome as any).player_name || (outcome as any).playerName || outcome.name;
              let btnText = resolveOutcomeName(market.market_id, outcome.outcome_id, locale, market.specifiers || {}, `${ev.event.home.name} - ${ev.event.away.name}`, outcomeDefaultName);
              if (btnText.toLowerCase().startsWith('over') || btnText.toLowerCase().startsWith('über') || btnText.toLowerCase().startsWith('üst')) btnText = '+';
              if (btnText.toLowerCase().startsWith('under') || btnText.toLowerCase().startsWith('unter') || btnText.toLowerCase().startsWith('alt')) btnText = '-';

              if (specLabel) {
                const escapedSpec = specLabel.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
                btnText = btnText.replace(new RegExp(`\\s*\\(\\s*${escapedSpec}\\s*\\)\\s*`, 'i'), '').trim();
              }

              const formattedLabel = formatOddsButtonLabel(btnText, ev.event.home.name, ev.event.away.name, locale);
              return { ...outcome, btnText, formattedLabel };
            });
            const orderedOutcomes = reorderPlusMinusPairs(processedOutcomes);

            return (
              <div key={`${market.market_id}-${specStr}`} className={`flex ${specLabel ? 'items-center' : 'flex-col'} w-full`}>
                {specLabel && (
                  <div className="w-[13%] pr-2 shrink-0 flex items-center justify-start">
                    <span className="text-[13px] font-bold text-[#555] leading-tight text-left">{specLabel}</span>
                  </div>
                )}
                <div className={`${specLabel ? 'flex-1' : 'w-full'} grid gap-1.5 ${activeOutcomes.length === 1
                    ? 'grid-cols-1'
                    : (activeOutcomes.length === 2 || activeOutcomes.length >= 4)
                      ? 'grid-cols-2'
                      : 'grid-cols-3'
                  }`}>
                  {orderedOutcomes.map((outcome) => {
                    const selected = isBetSelected(market.market_id, outcome.outcome_id, market.specifiers);
                    const oddsVal = outcome.active ? formatOdds(outcome.odds) : '-';

                    return (
                      <OddsButton
                        key={outcome.outcome_id}
                        disabled={oddsVal === '-'}
                        onClick={() => handleBetClick(market, outcome)} odds={outcome.odds} selected={selected}
                        className={`flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 w-full border ${selected
                          ? 'bg-[#333] border-[#333] text-white dark:bg-[#D41534] dark:border-[#D41534]'
                          : oddsVal === '-'
                            ? 'bg-gray-50 dark:bg-[#1a1a1a] opacity-40 cursor-not-allowed border-gray-200 dark:border-[#333] dark:text-gray-500'
                            : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0] dark:bg-[#252525] dark:border-[#333] dark:hover:bg-[#333] dark:text-white'
                          }`}
                      >
                        <span className={`text-[12px] font-medium truncate ${selected ? 'text-white' : 'text-[#555] dark:text-gray-300'}`}>
                          {outcome.formattedLabel}
                        </span>
                        <span className={`text-[14px] font-bold ${selected ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                          {oddsVal === '-' ? <LockIcon /> : oddsVal}
                        </span>
                      </OddsButton>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderBetBuilder = () => {
    const isLoggedIn = !!localStorage.getItem('access_token');

    if (bbLoading && !bbData) {
      return (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-red-600 border-t-transparent" />
        </div>
      );
    }

    if (bbError) {
      if (!isLoggedIn) {
        return (
          <div className="text-center py-12 px-6">
            <p className="text-[14px] text-gray-600 mb-4">Für Bet Builder+ bitte anmelden.</p>
            <button onClick={onLoginClick} className="px-6 py-2 rounded-full bg-[#333] text-white text-[14px] font-bold">
              Anmelden
            </button>
          </div>
        );
      }
      return <p className="text-center text-gray-400 text-sm py-12">Bet Builder ist derzeit nicht verfügbar.</p>;
    }

    if (bbData && (!bbData.custombet_available || bbData.markets.length === 0)) {
      return <p className="text-center text-gray-400 text-sm py-12">Bet Builder für dieses Spiel nicht verfügbar.</p>;
    }

    if (!bbData) return null;

    const getBbGroupKey = (m: CustomBetMarket) => {
      let key = m.market_id;
      const originalName = (m.name || "").toLowerCase();
      const appendAttrToTitle = (originalName.includes('goal') && originalName.includes('{!goalnr}')) ||
        (originalName.includes('corner') && originalName.includes('{!cornernr}')) ||
        m.outcomes.length >= 4;
      if (appendAttrToTitle && m.specifiers) {
        key = m.market_id + '-' + JSON.stringify(m.specifiers);
      }
      return key;
    };

    const bbGroups: Record<string, CustomBetMarket[]> = {};
    const bbUniqueGroups: CustomBetMarket[][] = [];
    const bbSeen = new Set<string>();

    bbData.markets.forEach(m => {
      const key = getBbGroupKey(m);
      if (!bbGroups[key]) bbGroups[key] = [];
      bbGroups[key].push(m);
    });

    bbData.markets.forEach(m => {
      const key = getBbGroupKey(m);
      if (!bbSeen.has(key)) {
        bbSeen.add(key);
        bbUniqueGroups.push(bbGroups[key]);
      }
    });

    return (
      <div className="relative">
        {bbUniqueGroups.map((marketGroup) => {
          if (!marketGroup || marketGroup.length === 0) return null;
          const firstMarket = marketGroup[0];

          const specMap = { ...(firstMarket.specifiers || {}) };
          if (specMap.player) {
            const playerOutcome = firstMarket.outcomes?.find((o: CustomBetOutcome) => (o as any).player_name || (o as any).playerName);
            if (playerOutcome) {
              specMap.playerName = (playerOutcome as any).player_name || (playerOutcome as any).playerName;
            }
          }

          let baseMarketName = resolveMarketName(
            firstMarket.market_id,
            locale,
            specMap,
            firstMarket.name,
            ev ? `${ev.event.home.name} - ${ev.event.away.name}` : undefined
          ) || firstMarket.name || "";

          if (baseMarketName) {
            baseMarketName = baseMarketName.replace(/\{[^}]+\}/g, '').trim();
          }

          const firstOriginalName = (firstMarket.name || "").toLowerCase();
          const hasSpecifiers = !!firstMarket.specifiers && Object.keys(firstMarket.specifiers).length > 0;
          const appendAttrToTitle = (firstOriginalName.includes('goal') && firstOriginalName.includes('{!goalnr}')) ||
            (firstOriginalName.includes('corner') && firstOriginalName.includes('{!cornernr}')) ||
            firstMarket.outcomes.length >= 4 ||
            (marketGroup.length === 1 && hasSpecifiers);

          if (appendAttrToTitle) {
            const firstSpecLabel = getSpecifierLabel(firstMarket as unknown as Market);
            if (firstSpecLabel) {
              baseMarketName = `${baseMarketName} (${firstSpecLabel})`;
            }
          }

          return (
            <div key={`bb-group-${firstMarket.market_id}-${appendAttrToTitle ? JSON.stringify(firstMarket.specifiers) : ''}`} className="px-3 py-2 border-b border-[#cbd5e1] bg-white flex flex-col">
              <div className="w-full mb-3 flex justify-start text-left">
                <span className="text-[14px] font-bold text-[#333] leading-tight">{baseMarketName}</span>
              </div>

              <div className="flex flex-col gap-2">
                {marketGroup.map((market) => {
                  const spec = market.specifiers_norm || '';
                  const outcomes = market.outcomes.filter((o: CustomBetOutcome) => o.custombet_available !== false);
                  if (outcomes.length === 0) return null;

                  let specLabel = getSpecifierLabel(market as unknown as Market);
                  const originalName = (market.name || "").toLowerCase();
                  const hideLeftAttr =
                    originalName.includes('exact') ||
                    originalName.includes('goal range') ||
                    originalName.includes('winning margin') ||
                    originalName.includes('when will') ||
                    originalName.includes('time of') ||
                    originalName.includes('goalscorer') ||
                    originalName.includes('correct score');

                  if (hideLeftAttr || appendAttrToTitle) {
                    specLabel = "";
                  }

                  return (
                    <div key={`bb-market-${market.market_id}-${spec}`} className={`flex ${specLabel ? 'items-center' : 'flex-col'} w-full`}>
                      {specLabel && (
                        <div className="w-[13%] pr-2 shrink-0 flex items-center justify-start">
                          <span className="text-[13px] font-bold text-[#555] leading-tight text-left">{specLabel}</span>
                        </div>
                      )}
                      <div className={`${specLabel ? 'flex-1' : 'w-full'} grid gap-1.5 ${outcomes.length === 1 ? 'grid-cols-1' : (outcomes.length === 2 || outcomes.length >= 4) ? 'grid-cols-2' : 'grid-cols-3'}`}>
                        {outcomes.map((o: CustomBetOutcome) => {
                          const selected = isBbOutcomeSelected(market.market_id, spec, o.outcome_id);
                          const incompatible = !selected && bbIncompatibleKeys.has(`${market.market_id}|${spec}|${o.outcome_id}`);
                          const disabled = incompatible || o.bettable === false;
                          const oddsVal = formatOdds(o.odds);

                          const outcomeDefaultName = (o as any).player_name || (o as any).playerName || o.name;
                          let btnText = resolveOutcomeName(market.market_id, o.outcome_id, locale, market.specifiers || {}, `${ev.event.home.name} - ${ev.event.away.name}`, outcomeDefaultName);
                          if (btnText.toLowerCase().startsWith('over') || btnText.toLowerCase().startsWith('über') || btnText.toLowerCase().startsWith('üst')) btnText = '+';
                          if (btnText.toLowerCase().startsWith('under') || btnText.toLowerCase().startsWith('unter') || btnText.toLowerCase().startsWith('alt')) btnText = '-';

                          if (specLabel) {
                            const escapedSpec = specLabel.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
                            btnText = btnText.replace(new RegExp(`\\s*\\(\\s*${escapedSpec}\\s*\\)\\s*`, 'i'), '').trim();
                          }

                          const formattedLabel = formatOddsButtonLabel(btnText, ev.event.home.name, ev.event.away.name, locale);

                          return (
                            <button
                              key={o.outcome_id}
                              disabled={disabled}
                              onClick={() => toggleBbOutcome(market.market_id, spec, o.outcome_id)}
                              className={`flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 w-full border ${selected
                                  ? 'bg-[#333] border-[#333] text-white dark:bg-[#D41534] dark:border-[#D41534]'
                                  : disabled
                                    ? 'bg-gray-50 dark:bg-[#1a1a1a] opacity-40 cursor-not-allowed border-gray-200 dark:border-[#333] dark:text-gray-500'
                                    : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0] dark:bg-[#252525] dark:border-[#333] dark:hover:bg-[#333] dark:text-white'
                                }`}
                            >
                              <span className={`text-[12px] font-medium truncate ${selected ? 'text-white' : 'text-[#555] dark:text-gray-300'}`}>
                                {formattedLabel}
                              </span>
                              <span className={`text-[14px] font-bold ${selected ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                                {oddsVal === '-' ? <LockIcon /> : oddsVal}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-full bg-gray-50 flex flex-col pb-20">

      {/* ── Fixed zone: header + tabs ─────────────────────────────────── */}
      <div className={`sticky top-0 z-[110] shadow-sm transition-colors duration-200 ${heroVisible ? 'bg-white' : 'bg-[#282828]'}`}>

        {/* Header row */}
        <div className={`px-4 py-3 flex items-center gap-2 border-b ${heroVisible ? 'border-gray-100' : 'border-gray-800'}`}>
          <button onClick={onBack} className={`p-1 flex-shrink-0 ${heroVisible ? 'text-gray-800' : 'text-white hover:text-gray-300'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Normal: tournament name. Scrolled: compact match info */}
          {heroVisible ? (
            <span className="text-sm font-semibold text-gray-700 truncate mx-2 flex-1">
              {ev.event.tournament_name}, {ev.event.category_name}
            </span>
          ) : (
            <div className="flex items-center justify-center gap-2 flex-1 mx-2 min-w-0">
              {ev.event.home.crest_url ? (
                <img src={ev.event.home.crest_url} alt="" className="w-5 h-5 object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              ) : (
                <JerseyIcon color="#c0392b" className="w-5 h-5 flex-shrink-0 rounded-[4px]" />
              )}
              <span className="text-xs font-semibold text-white truncate max-w-[70px] text-right">{ev.event.home.name}</span>
              <div className="flex flex-col items-center flex-shrink-0 mx-1">
                {isLive ? (
                  <>
                    <span className="text-[10px] font-bold text-red-500 leading-none">Live</span>
                    <span className="text-base font-black text-white leading-tight">
                      {ev.status?.home_score ?? 0}:{ev.status?.away_score ?? 0}
                    </span>
                  </>
                ) : (
                  <span className="text-base font-black text-white">
                    {new Date(ev.event.scheduled_at ?? ev.event.start_time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
              <span className="text-xs font-semibold text-white truncate max-w-[70px] text-left">{ev.event.away.name}</span>
              {ev.event.away.crest_url ? (
                <img src={ev.event.away.crest_url} alt="" className="w-5 h-5 object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              ) : (
                <JerseyIcon color="#2c3e50" className="w-5 h-5 flex-shrink-0 rounded-[4px]" />
              )}
            </div>
          )}

          <button
            onClick={() => toggleFavorite(ev.event_id)}
            className="ml-auto p-1 flex-shrink-0 text-gray-400 hover:text-yellow-400"
          >
            <svg className={`w-6 h-6 ${isFavorite(ev.event_id) ? 'text-yellow-400 fill-current' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </button>
        </div>

      </div>

      {/* Sentinel for IntersectionObserver */}
      <div ref={heroRef} className="h-0 w-full" />

      {/* ── Match Hero (not sticky) ────────────────────────────────────── */}
      <div className="bg-[#282828] md:pt-0 pt-1 border-b border-gray-800">
        <div className="flex items-center justify-between gap-4">
          {/* Home Team */}
          <div className="flex flex-col items-center gap-0 flex-1 min-w-0">
            {ev.event.home.crest_url ? (
              <img src={ev.event.home.crest_url} alt="" className="w-[2rem] h-[2rem] object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            ) : (
              <JerseyIcon color="#c0392b" className="w-[2rem] h-[2rem] flex-shrink-0 rounded-[4px]" />
            )}
            <span className="text-[0.775rem] font-semibold text-white text-center leading-tight line-clamp-2">
              {ev.event.home.name}
            </span>
            {isLive && ev.status?.statistics && (
              <div className="flex items-center gap-3 text-xs text-gray-400 bg-gray-900/50 px-2.5 py-1 rounded-lg mt-0.5">
                {(ev.status.statistics.corners?.home !== undefined) && (
                  <span className="flex items-center gap-1">⛳ {ev.status.statistics.corners.home}</span>
                )}
                {(ev.status.statistics.yellow_cards?.home !== undefined) && (
                  <span className="flex items-center gap-1">🟨 {ev.status.statistics.yellow_cards.home}</span>
                )}
                {(ev.status.statistics.red_cards?.home !== undefined && ev.status.statistics.red_cards.home > 0) && (
                  <span className="flex items-center gap-1">🟥 {ev.status.statistics.red_cards.home}</span>
                )}
              </div>
            )}
          </div>

          {/* Score / Time */}
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            {isLive ? (
              <>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-3xl font-black text-white">{ev.status?.home_score ?? 0}</span>
                  <span className="text-xl font-bold text-gray-400">:</span>
                  <span className="text-3xl font-black text-white">{ev.status?.away_score ?? 0}</span>
                </div>
                <span className="text-[0.875rem] font-bold text-white">{formatClock(ev, t)}</span>
              </>
            ) : (
              <>
                <span className="text-[0.875rem] font-black text-white">
                  {new Date(ev.event.scheduled_at ?? ev.event.start_time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="text-[0.875rem] text-gray-300">
                  {(() => {
                    const d = new Date(ev.event.scheduled_at ?? ev.event.start_time);
                    const now = new Date();
                    const tomorrow = new Date(now);
                    tomorrow.setDate(now.getDate() + 1);
                    if (d.toDateString() === now.toDateString()) return 'heute';
                    if (d.toDateString() === tomorrow.toDateString()) return 'morgen';
                    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
                  })()}
                </span>
                <span className="text-xs text-gray-400 mt-1">vs</span>
              </>
            )}
          </div>

          {/* Away Team */}
          <div className="flex flex-col items-center gap-0 flex-1 min-w-0">
            {ev.event.away.crest_url ? (
              <img src={ev.event.away.crest_url} alt="" className="w-[2rem] h-[2rem] object-contain flex-shrink-0 rounded-[4px]" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            ) : (
              <JerseyIcon color="#2c3e50" className="w-[2rem] h-[2rem] flex-shrink-0 rounded-[4px]" />
            )}
            <span className="text-[0.775rem] font-semibold text-white text-center leading-tight line-clamp-2">
              {ev.event.away.name}
            </span>
            {isLive && ev.status?.statistics && (
              <div className="flex items-center gap-3 text-xs text-gray-400 bg-gray-900/50 px-2.5 py-1 rounded-lg mt-0.5">
                {(ev.status.statistics.corners?.away !== undefined) && (
                  <span className="flex items-center gap-1">⛳ {ev.status.statistics.corners.away}</span>
                )}
                {(ev.status.statistics.yellow_cards?.away !== undefined) && (
                  <span className="flex items-center gap-1">🟨 {ev.status.statistics.yellow_cards.away}</span>
                )}
                {(ev.status.statistics.red_cards?.away !== undefined && ev.status.statistics.red_cards.away > 0) && (
                  <span className="flex items-center gap-1">🟥 {ev.status.statistics.red_cards.away}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Period scores */}
        {isLive && ev.status?.period_scores && ev.status.period_scores.length > 0 && (
          <div className="md:mt-0 flex justify-center gap-2">
            {ev.status.period_scores.map((p) => (
              <div key={p.number} className="flex items-center gap-1.5">
                <span className="text-xs text-gray-400">{p.number}. HZ</span>
                <span className="text-sm font-bold text-gray-300">({p.home_score}:{p.away_score})</span>
              </div>
            ))}
          </div>
        )}


        {/* Panel Buttons */}
        <div className="flex items-center justify-between mt-2 bg-gray-900 rounded-xl p-1">
          {streamUrl && (
            <button
              type="button"
              onClick={() => handlePanelClick('stream')}
              className={`flex-1 flex items-center justify-center rounded-lg py-2 transition ${activePanel === 'stream' ? 'bg-[#282828] text-red-500 shadow-sm' : 'text-gray-400 hover:text-white'}`}
            >
              <svg viewBox="0 0 18 13" className="w-6 h-6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.5" y="0.5" width="16.6154" height="12" stroke="currentColor"></rect>
                <path d="M6.96155 3.26923V9.73076L12.0385 6.5L6.96155 3.26923Z" fill="currentColor"></path>
              </svg>
            </button>
          )}
          <button
            type="button"
            onClick={() => handlePanelClick('tracker')}
            className={`flex-1 flex items-center justify-center rounded-lg py-2 transition ${activePanel === 'tracker' ? 'bg-[#282828] text-red-500 shadow-sm' : 'text-gray-400 hover:text-white'}`}
          >
            <svg viewBox="0 0 20 20" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.45 17.5H2.55a.884.884 0 01-.883-.884V3.384c0-.488.396-.884.884-.884h14.9c.487 0 .882.396.882.884v13.233a.883.883 0 01-.884.883v0z" />
              <path d="M4.167 12.5h-2.5v-5h2.5c.46 0 .833.373.833.833v3.334c0 .46-.373.833-.833.833zm14.166 0h-2.5a.833.833 0 01-.833-.833V8.333c0-.46.373-.833.833-.833h2.5v5zm-6.565-4.268a2.501 2.501 0 11-3.538 3.536 2.501 2.501 0 013.538-3.536zM10 2.5v5m0 5.5v4" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => handlePanelClick('stats')}
            className={`flex-1 flex items-center justify-center rounded-lg py-2 transition ${activePanel === 'stats' ? 'bg-[#282828] text-red-500 shadow-sm' : 'text-gray-400 hover:text-white'}`}
          >
            <svg viewBox="0 0 20 20" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <g clipPath="url(#clip0)">
                <path d="M14.166 17.5H17.5V3.333a.833.833 0 00-.834-.833H15a.834.834 0 00-.834.833V17.5zm-5.832 0h3.333V8.07a.833.833 0 00-.833-.833H9.167a.833.833 0 00-.833.833v9.43zm-5.834 0h3.333v-4.693A.834.834 0 005 11.973H3.333a.834.834 0 00-.833.834V17.5z" />
              </g>
              <defs>
                <clipPath id="clip0">
                  <path fill="#fff" d="M0 0h20v20H0z" />
                </clipPath>
              </defs>
            </svg>
          </button>
          <button
            type="button"
            onClick={() => handlePanelClick('lineup')}
            className={`flex-1 flex items-center justify-center rounded-lg py-2 transition ${activePanel === 'lineup' ? 'bg-[#282828] text-red-500 shadow-sm' : 'text-gray-400 hover:text-white'}`}
          >
            <svg viewBox="0 0 20 20" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 10h2.116c.46 0 .833-.373.833-.833V5.913a2.5 2.5 0 00-1.416-2.253l-3.075-1.48a1.668 1.668 0 00-1.901.323l-.511.511a1.666 1.666 0 01-2.357 0l-.517-.518a1.666 1.666 0 00-1.89-.328L3.483 3.49a2.498 2.498 0 00-1.432 2.26v3.417c0 .46.373.833.833.833H5" />
              <path d="M5.417 7.5v9.167c0 .46.373.833.833.833h7.5c.46 0 .833-.373.833-.833V7.5 10" />
              <path fill="currentColor" stroke="none" d="M8.328 13.333v-.861H9.63V8.64H8.558v-.66c.293-.055.543-.12.751-.192.214-.08.413-.175.596-.285h.788v4.969h1.146v.861H8.33z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => handlePanelClick('timeline')}
            className={`flex-1 flex items-center justify-center rounded-lg py-2 transition ${activePanel === 'timeline' ? 'bg-[#282828] text-red-500 shadow-sm' : 'text-gray-400 hover:text-white'}`}
          >
            <svg viewBox="0 0 20 20" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <g clipPath="url(#clip0-timeline)">
                <path d="M8.334 1.667h3.333m3.047 4.453a6.664 6.664 0 010 9.427 6.667 6.667 0 110-9.428M10 7.5v3.333m6.667-6.666L15 5.833m.834-2.5L17.5 5" />
              </g>
              <defs>
                <clipPath id="clip0-timeline">
                  <path fill="#fff" d="M0 0h20v20H0z" />
                </clipPath>
              </defs>
            </svg>
          </button>
        </div>
      </div>

      {activePanel === 'stream' && (
        <div className="w-full bg-[#282828] relative h-[340px] md:h-[300px]">
          {streamUrl ? (
            <iframe
              src={streamUrl}
              className="w-full h-full border-0"
              allowFullScreen
              allow="autoplay; fullscreen"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-white text-sm font-semibold">
              Stream aktuell nicht verfügbar.
            </div>
          )}
        </div>
      )}

      {activePanel === 'tracker' && (
        <div className="w-full bg-[#282828] relative h-[340px] md:h-[300px]">
          <iframe
            src={trackerUrl}
            className="w-full h-full border-0"
            allowFullScreen
            allow="autoplay; fullscreen"
          />
        </div>
      )}

      {activePanel === 'stats' && (
        <div className="bg-white border-b border-gray-100 px-4 py-4" style={{ minHeight: '300px' }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Liga-Statistiken</div>
              <div className="text-xs text-gray-500">{ev.event.tournament_name} · {ev.event.category_name}</div>
            </div>
            <span className="text-xs font-semibold text-red-600 uppercase">{leagueEvents ? `${leagueEvents.length} Spiele` : 'Lädt...'}</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-gray-50 p-3">
              <div className="text-xs text-gray-500 uppercase tracking-[0.2em] mb-2">{t('section.nextGames') || 'Nächste Spiele'}</div>
              {leagueEvents?.length ? (
                leagueEvents.slice(0, 3).map((item) => (
                  <div key={item.event_id} className="text-sm text-gray-700 leading-tight">
                    {item.event.home.name} vs {item.event.away.name}
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500">Keine Daten verfügbar.</div>
              )}
            </div>
            <div className="rounded-xl bg-gray-50 p-3">
              <div className="text-xs text-gray-500 uppercase tracking-[0.2em] mb-2">Aktueller Matchstatus</div>
              <div className="text-sm text-gray-700">
                {isLive ? (
                  <>
                    <div>Live: {formatClock(ev, t)}</div>
                    <div>Tore: {ev.status.home_score ?? 0}:{ev.status.away_score ?? 0}</div>
                  </>
                ) : (
                  <div>Geplant für {formatMatchTime(ev)}</div>
                )}
              </div>
            </div>
            <div className="rounded-xl bg-gray-50 p-3 sm:col-span-2">
              <div className="text-xs text-gray-500 uppercase tracking-[0.2em] mb-2">Spielstatistik</div>
              {ev.status?.statistics ? (
                <div className="grid grid-cols-3 gap-3 text-sm text-gray-700">
                  <div className="rounded-lg bg-white p-3 text-center">
                    <div className="font-semibold">Ecken</div>
                    <div>{ev.status.statistics.corners?.home ?? 0}:{ev.status.statistics.corners?.away ?? 0}</div>
                  </div>
                  <div className="rounded-lg bg-white p-3 text-center">
                    <div className="font-semibold">Gelb</div>
                    <div>{ev.status.statistics.yellow_cards?.home ?? 0}:{ev.status.statistics.yellow_cards?.away ?? 0}</div>
                  </div>
                  <div className="rounded-lg bg-white p-3 text-center">
                    <div className="font-semibold">Rot</div>
                    <div>{ev.status.statistics.red_cards?.home ?? 0}:{ev.status.statistics.red_cards?.away ?? 0}</div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">Keine Spielstatistiken verfügbar.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Market view tabs: All Markets vs Bet Builder+ (Only for Soccer and if CustomBet is available) */}
      {ev?.event.sport_id === 'sr:sport:1' && bbData && bbData.custombet_available && bbData.markets.length > 0 && (
        <div className="flex gap-2 px-4 py-3 bg-white border-b border-[#d8dce0]">
          {([['all', t('match.allMarkets')], ['builder', t('match.betBuilderPlus')]] as const).map(([tabId, label]) => (
            <button
              key={tabId}
              onClick={() => setMarketTab(tabId)}
              className={`flex-1 px-4 py-2 rounded-full text-[14px] font-bold border transition ${marketTab === tabId ? 'bg-[#333] text-white border-[#333]' : 'bg-white text-gray-700 border-gray-300'
                }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="bg-[#f0f2f5] min-h-screen">
        {marketTab === 'builder' ? renderBetBuilder() : (
          <>
            {marketsByGroup.length === 0 && (
              <p className="text-center text-gray-400 text-sm mt-10">Keine Wetten verfügbar.</p>
            )}

            {/* Render grouped markets by heading */}
            {marketsByGroup.map((groupData) => {
              const isGroupCollapsed = collapsedGroups[groupData.groupName] ?? (groupData.groupName !== 'Main');
              const toggleGroup = () => setCollapsedGroups(prev => ({ ...prev, [groupData.groupName]: !isGroupCollapsed }));

              return (
                <div key={`supergroup-${groupData.groupName}`} className="mb-0 border-b-2 border-[#d8dce0]">
                  <button
                    onClick={toggleGroup}
                    className="w-full px-4 py-[14px] bg-[#f8f9fa] border-b border-[#cbd5e1] flex items-center justify-between"
                  >
                    <span className="text-[15px] font-bold text-[#333] capitalize">{groupData.label}</span>
                    <svg className={`w-5 h-5 text-gray-400 transition-transform ${isGroupCollapsed ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {!isGroupCollapsed && (
                    <div className="bg-white">
                      {groupData.markets.map((marketGroup: Market[]) => renderMarketGroup(marketGroup))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default MatchDetailPage;
